// src/components/AccountantSalaryDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Comment as CommentIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';

import Sidebar from '../../components/sidebars/Sidebar_auditor';
import TopNav from '../../components/top_bars/TopNav_auditor';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

const AuditorSalaryDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [allSalaries, setAllSalaries] = useState([]);
  const [stats, setStats] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('salary_desc');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      
      // Fetch all salaries
      const allRes = await fetch(`${baseUrl}/update_salary.php?type=all`);
      const allResult = await allRes.json();
      if (allResult.success) {
        setAllSalaries(allResult.data);
      }
      
      // Fetch statistics
      const statsRes = await fetch(`${baseUrl}/update_salary.php?type=stats`);
      const statsResult = await statsRes.json();
      if (statsResult.success) {
        setStats(statsResult.stats);
        setDepartmentStats(statsResult.department_stats);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaryHistory = async (phone) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/get_salary_history.php?phone=${phone}`);
      const result = await response.json();
      if (result.success) {
        setSalaryHistory(result.data);
      }
    } catch (err) {
      console.error('Error fetching salary history:', err);
    }
  };

  const handleViewStaff = async (staff) => {
    setSelectedStaff(staff);
    await fetchSalaryHistory(staff.phone);
    setViewDialogOpen(true);
  };

  const handleViewHistory = async (staff) => {
    setSelectedStaff(staff);
    await fetchSalaryHistory(staff.phone);
    setHistoryDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₦0.00';
    const roundedAmount = Math.round(amount * 100) / 100;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(roundedAmount);
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return new Intl.NumberFormat('en-NG').format(Math.round(num));
  };

  // Filter and sort staff salaries
  const getFilteredSalaries = () => {
    let filtered = [...allSalaries];
    
    if (searchTerm) {
      filtered = filtered.filter(s => 
        s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.phone?.includes(searchTerm) ||
        s.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(s => s.department === departmentFilter);
    }
    
    switch (sortBy) {
      case 'salary_desc':
        filtered.sort((a, b) => (b.salary_amount || 0) - (a.salary_amount || 0));
        break;
      case 'salary_asc':
        filtered.sort((a, b) => (a.salary_amount || 0) - (b.salary_amount || 0));
        break;
      case 'name_asc':
        filtered.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
        break;
      case 'name_desc':
        filtered.sort((a, b) => (b.full_name || '').localeCompare(a.full_name || ''));
        break;
      default:
        break;
    }
    
    return filtered;
  };

  const exportToCSV = () => {
    const headers = ['Staff Name', 'Department', 'Phone', 'Email', 'Salary Amount', 'Bank', 'Account Number', 'Account Name'];
    const csvData = getFilteredSalaries().map(s => [
      s.full_name,
      s.department,
      s.phone,
      s.email,
      s.salary_amount,
      s.bank_name,
      s.account_number,
      s.account_name
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  const filteredSalaries = getFilteredSalaries();
  const uniqueDepartments = ['all', ...new Set(allSalaries.map(s => s.department).filter(Boolean))];
  
  // Prepare chart data
  const pieChartData = departmentStats.map(dept => ({
    name: dept.department,
    value: dept.total_salary,
    staffCount: dept.staff_count
  }));

  const totalPayroll = stats?.total_monthly_payroll || 0;
  const averageSalary = stats?.average_salary || 0;
  const minSalary = stats?.min_salary || 0;
  const maxSalary = stats?.max_salary || 0;

  // Salary band distribution
  const salaryBands = [
    { min: 0, max: 50000, label: '₦0 - ₦50k', count: 0, color: '#4caf50' },
    { min: 50001, max: 100000, label: '₦50k - ₦100k', count: 0, color: '#2196f3' },
    { min: 100001, max: 200000, label: '₦100k - ₦200k', count: 0, color: '#ff9800' },
    { min: 200001, max: 300000, label: '₦200k - ₦300k', count: 0, color: '#9c27b0' },
    { min: 300001, max: 500000, label: '₦300k - ₦500k', count: 0, color: '#f44336' },
    { min: 500001, max: Infinity, label: '₦500k+', count: 0, color: '#795548' },
  ];

  allSalaries.forEach(s => {
    const salary = s.salary_amount || 0;
    const band = salaryBands.find(b => salary >= b.min && salary <= b.max);
    if (band) band.count++;
  });

  const StatCard = ({ title, value, subtitle, color, icon: Icon }) => (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="textSecondary">{title}</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{value}</Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && allSalaries.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading salary data...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, maxWidth: '1600px', mx: 'auto', width: '100%', overflow: 'auto', height: '100vh' }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, background: `linear-gradient(135deg, #1a237e 0%, #283593 100%)`, color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">Audit View</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>Financial overview, payroll management, and salary analytics</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" startIcon={<PrintIcon />} onClick={printReport} sx={{ bgcolor: 'white', color: '#1a237e', '&:hover': { bgcolor: '#f5f5f5' } }}>
                Print Report
              </Button>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportToCSV} sx={{ bgcolor: 'white', color: '#1a237e', '&:hover': { bgcolor: '#f5f5f5' } }}>
                Export CSV
              </Button>
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchAllData} sx={{ bgcolor: 'white', color: '#1a237e', '&:hover': { bgcolor: '#f5f5f5' } }}>
                Refresh
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Financial Overview Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total Monthly Payroll" value={formatCurrency(totalPayroll)} subtitle="Across all departments" color="#4caf50" icon={MoneyIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Annual Payroll" value={formatCurrency(totalPayroll * 12)} subtitle="Yearly projection" color="#2196f3" icon={ReceiptIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Average Salary" value={formatCurrency(averageSalary)} subtitle="Per employee" color="#ff9800" icon={AssessmentIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total Staff" value={formatNumber(allSalaries.length)} subtitle="Active employees" color="#9c27b0" icon={PersonIcon} />
          </Grid>
        </Grid>

        {/* Salary Range Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ borderRadius: 3, bgcolor: '#e8f5e9' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary">Highest Salary</Typography>
                <Typography variant="h5" fontWeight="bold" color="#4caf50">{formatCurrency(maxSalary)}</Typography>
                <Typography variant="caption">Top earner</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ borderRadius: 3, bgcolor: '#fff3e0' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary">Lowest Salary</Typography>
                <Typography variant="h5" fontWeight="bold" color="#ff9800">{formatCurrency(minSalary)}</Typography>
                <Typography variant="caption">Base salary</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ borderRadius: 3, bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary">Salary Range</Typography>
                <Typography variant="h5" fontWeight="bold" color="#2196f3">{formatCurrency(maxSalary - minSalary)}</Typography>
                <Typography variant="caption">Difference</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Section - Two Rows */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>Payroll Distribution by Department</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={departmentStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="total_salary" fill="#1a237e" name="Total Salary" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>Staff Count by Department</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={departmentStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="staff_count" fill="#ff9800" name="Staff Count" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>Salary Distribution Percentage</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                    {pieChartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>Salary Band Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salaryBands}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#4caf50" name="Employees">
                    {salaryBands.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                Number of employees per salary bracket
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Department Statistics Table */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>Department Financial Summary</Typography>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Department</TableCell>
                  <TableCell align="right">Staff Count</TableCell>
                  <TableCell align="right">Total Salary</TableCell>
                  <TableCell align="right">Average Salary</TableCell>
                  <TableCell align="right">% of Payroll</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departmentStats.map((dept, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{dept.department}</TableCell>
                    <TableCell align="right">{formatNumber(dept.staff_count)}</TableCell>
                    <TableCell align="right">{formatCurrency(dept.total_salary)}</TableCell>
                    <TableCell align="right">{formatCurrency(dept.avg_salary)}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${totalPayroll > 0 ? ((dept.total_salary / totalPayroll) * 100).toFixed(1) : 0}%`}
                        size="small"
                        sx={{ bgcolor: alpha('#1a237e', 0.1), color: '#1a237e' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Staff Salary Table */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6">Staff Salary Register</Typography>
          </Box>
          
          {/* Filters */}
          <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              size="small"
              placeholder="Search by name, department, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 2, minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Department</InputLabel>
              <Select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} label="Department">
                {uniqueDepartments.map((dept, idx) => (
                  <MenuItem key={idx} value={dept}>{dept === 'all' ? 'All Departments' : dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sort By">
                <MenuItem value="salary_desc">Highest Salary First</MenuItem>
                <MenuItem value="salary_asc">Lowest Salary First</MenuItem>
                <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                <MenuItem value="name_desc">Name (Z-A)</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
              Total: {formatNumber(filteredSalaries.length)} staff | Monthly: {formatCurrency(filteredSalaries.reduce((sum, s) => sum + (s.salary_amount || 0), 0))}
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Staff Name</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell align="right">Salary Amount</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Account Number</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSalaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1" color="textSecondary">No salary records found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSalaries.map((staff, index) => (
                    <TableRow key={staff.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#1a237e', width: 32, height: 32 }}><PersonIcon sx={{ fontSize: 18 }} /></Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">{staff.full_name || 'N/A'}</Typography>
                            <Typography variant="caption" color="textSecondary">{staff.email || 'N/A'}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Chip size="small" label={staff.department || 'N/A'} variant="outlined" /></TableCell>
                      <TableCell>{staff.phone || 'N/A'}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="#1a237e">{formatCurrency(staff.salary_amount)}</Typography>
                      </TableCell>
                      <TableCell>{staff.bank_name || 'Not set'}</TableCell>
                      <TableCell>{staff.account_number || 'Not set'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewStaff(staff)} sx={{ color: '#1a237e' }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Salary History">
                          <IconButton size="small" onClick={() => handleViewHistory(staff)} sx={{ color: '#ff9800' }}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Typography variant="body2" color="textSecondary">© {new Date().getFullYear()} Elegantè IT - Accountant Dashboard</Typography>
        </footer>
      </Box>

      {/* View Staff Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedStaff && (
          <>
            <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Staff Financial Details</Typography>
                <IconButton onClick={() => setViewDialogOpen(false)} sx={{ color: 'white' }}><CancelIcon /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Personal Information</Typography>
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      <Box><strong>Name:</strong> {selectedStaff.full_name}</Box>
                      <Box><strong>Department:</strong> {selectedStaff.department}</Box>
                      <Box><strong>Phone:</strong> {selectedStaff.phone}</Box>
                      <Box><strong>Email:</strong> {selectedStaff.email}</Box>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Banking Information</Typography>
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      <Box><strong>Bank:</strong> {selectedStaff.bank_name || 'Not set'}</Box>
                      <Box><strong>Account Number:</strong> {selectedStaff.account_number || 'Not set'}</Box>
                      <Box><strong>Account Name:</strong> {selectedStaff.account_name || 'Not set'}</Box>
                      <Box><strong>Current Salary:</strong> <span style={{ fontWeight: 'bold', color: '#1a237e' }}>{formatCurrency(selectedStaff.salary_amount)}</span></Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)} variant="outlined">Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Salary History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedStaff && (
          <>
            <DialogTitle sx={{ bgcolor: '#ff9800', color: 'white' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Salary History - {selectedStaff.full_name}</Typography>
                <IconButton onClick={() => setHistoryDialogOpen(false)} sx={{ color: 'white' }}><CancelIcon /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              {salaryHistory.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>No salary history records found.</Alert>
              ) : (
                <TableContainer sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Previous Salary</TableCell>
                        <TableCell align="right">New Salary</TableCell>
                        <TableCell align="right">Increment</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Approved By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salaryHistory.map((record, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{new Date(record.created_at).toLocaleDateString()}</TableCell>
                          <TableCell align="right">{formatCurrency(record.previous_salary)}</TableCell>
                          <TableCell align="right"><strong>{formatCurrency(record.new_salary)}</strong></TableCell>
                          <TableCell align="right" sx={{ color: '#4caf50' }}>+{formatCurrency(record.increment_amount)}</TableCell>
                          <TableCell>{record.reason || 'N/A'}</TableCell>
                          <TableCell>{record.approved_by || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setHistoryDialogOpen(false)} variant="outlined">Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={snackbar?.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AuditorSalaryDashboard;